/**
 * Hex coordinate math for Game of Kings.
 *
 * Coordinates are cube coordinates `(q, r, s)` with the invariant `q + r + s === 0`.
 * The board is rendered pointy-top, and the pixel layout reproduces the geometry the
 * 2023 build got from honeycomb-grid, so board art and saved viewBoxes still line up:
 *
 *     x = sqrt(3) * (q + r / 2)
 *     y = 1.5 * r
 *
 * No dependencies, by design — this module is imported by the server, the browser,
 * the bot worker and the test harness alike.
 */

/** A cell coordinate. `s` is redundant (`-q - r`) but carried for readability. */
export interface Hex {
  readonly q: number;
  readonly r: number;
  readonly s: number;
}

/**
 * The six neighbour directions, in rotational order (clockwise on screen, where y
 * grows downward). Starting direction is NE, matching the 2023 build.
 *
 * IMPORTANT: the bow rule is defined in terms of this ordering — it reads neighbours
 * at `(dir + 2) % 6`, `(dir + 3) % 6` and `(dir + 4) % 6` to find the 120-degree
 * elbow. Reordering or reversing this array silently changes the rules of the game.
 */
export const DIRECTIONS: readonly Hex[] = Object.freeze([
  { q: 1, r: -1, s: 0 }, // 0 NE
  { q: 1, r: 0, s: -1 }, // 1 E
  { q: 0, r: 1, s: -1 }, // 2 SE
  { q: -1, r: 1, s: 0 }, // 3 SW
  { q: -1, r: 0, s: 1 }, // 4 W
  { q: 0, r: -1, s: 1 }, // 5 NW
]);

export const DIRECTION_COUNT = 6;

/** Opposite of direction `d`. */
export const opposite = (d: number): number => (d + 3) % 6;

export const hex = (q: number, r: number): Hex => ({ q, r, s: -q - r });

export const add = (a: Hex, b: Hex): Hex => hex(a.q + b.q, a.r + b.r);

export const scale = (a: Hex, k: number): Hex => hex(a.q * k, a.r * k);

export const neighbor = (a: Hex, d: number): Hex => add(a, DIRECTIONS[d]);

export const equals = (a: Hex, b: Hex): boolean => a.q === b.q && a.r === b.r;

/** Distance in cells between two hexes. */
export const distance = (a: Hex, b: Hex): number =>
  (Math.abs(a.q - b.q) + Math.abs(a.r - b.r) + Math.abs(a.s - b.s)) / 2;

/** Rings from the origin: `0` for the centre cell. */
export const ringOf = (a: Hex): number => distance(a, ORIGIN);

export const ORIGIN: Hex = { q: 0, r: 0, s: 0 };

/**
 * The canonical string key for a cell: `"q,r,s"`.
 *
 * This is the identity used in formations, tutorial scripts and perft golden files,
 * precisely so that none of them depend on the board's internal cell ordering.
 */
export const key = (a: Hex): string => `${a.q},${a.r},${a.s}`;

export const parseKey = (k: string): Hex => {
  const parts = k.split(',');
  if (parts.length !== 3) {
    throw new Error(`Malformed hex key ${JSON.stringify(k)}: expected "q,r,s"`);
  }
  const [q, r, s] = parts.map((p) => {
    const n = Number(p);
    if (!Number.isInteger(n)) {
      throw new Error(`Malformed hex key ${JSON.stringify(k)}: ${p} is not an integer`);
    }
    return n;
  });
  if (q + r + s !== 0) {
    throw new Error(`Invalid hex key ${JSON.stringify(k)}: q + r + s must be 0`);
  }
  return { q, r, s };
};

const SQRT3 = Math.sqrt(3);

/** Centre of a cell in board units (one cell is 2 units tall, sqrt(3) units wide). */
export const toPixel = (a: Hex): { x: number; y: number } => ({
  x: SQRT3 * (a.q + a.r / 2),
  y: 1.5 * a.r,
});

/**
 * The six corners of a cell, relative to its centre, for rendering. Circumradius is 1,
 * which makes a cell `sqrt(3)` wide and `2` tall — consistent with the `toPixel`
 * spacing above, so cells tile without gaps.
 */
export const CORNERS: readonly { x: number; y: number }[] = Object.freeze(
  Array.from({ length: 6 }, (_, i) => {
    const angle = (Math.PI / 180) * (60 * i - 30);
    return {
      x: Number(Math.cos(angle).toFixed(10)),
      y: Number(Math.sin(angle).toFixed(10)),
    };
  }),
);

/**
 * All cells within `radius` of the origin, ordered centre-outward: ring 0, then each
 * ring walked from its NE corner in rotational order.
 *
 * The ordering is deliberate and documented, but nothing in the rules depends on it —
 * cells are addressed by coordinate everywhere it matters.
 */
export function* spiral(radius: number): Generator<Hex> {
  if (!Number.isInteger(radius) || radius < 0) {
    throw new Error(`Board radius must be a non-negative integer, got ${radius}`);
  }

  yield ORIGIN;

  for (let ring = 1; ring <= radius; ring++) {
    // Start at the NE corner, then walk each of the six edges.
    let cur = scale(DIRECTIONS[0], ring);
    for (let side = 0; side < 6; side++) {
      // Edge `side` runs in the direction two steps around from the corner it starts at.
      const step = DIRECTIONS[(side + 2) % 6];
      for (let i = 0; i < ring; i++) {
        yield cur;
        cur = add(cur, step);
      }
    }
  }
}

/** Number of cells on a board of the given radius: the centred hexagonal numbers. */
export const cellCount = (radius: number): number => 3 * radius * radius + 3 * radius + 1;
